import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { faRotateRight, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

import { crmPostventaIpcApi } from "../crmPostventaIpcApi";
import Alert from "../../crmpostventa-shared/components/Alert";
import Modal from "../../crmpostventa-shared/components/Modal";
import useIsMobile from "../../crmpostventa-shared/useIsMobile";
import { EMPTY, codeOf, formatCuit, formatDate, formatDuration, formatRelativeDays, labelFor, toIsoDate, todayIso } from "../../crmpostventa-shared/crmFormat";
import { buildWhatsAppUrl } from "../../crmpostventa/zoco-check-alerts/zocoCheckAlertsUtils";
import { MOTIVO_LABELS, ORIGEN_LABELS, TIPO_LABELS, daysBetween, isMine, isProactiva, motivoLabel } from "../ipcFormat";
import {
  Actor,
  HistoricoChip,
  IpcStatusChip,
  Muted,
  PrioridadCell,
  ProximoContactoCell,
  TipoChip,
  canalLabel,
  dateTime,
  motivoCierreLabel,
} from "../components/ipcCells";
import AsignarGestionDialog from "../gestionar/AsignarGestionDialog";
import { LinksBand, Timeline } from "../gestionar/GestionarIpcParts";
import { CONFLICT_TITLE, freshEntryIds, ownershipFor } from "../gestionar/gestionarIpcModel";
import useOwnershipCommands from "../gestionar/useOwnershipCommands";
import { closingObservacion, comercioTieneAbiertas, detalleStage, historicoDetalle, isAbierta, otraFecha } from "./detalleIpcModel";

const TITLE_ID = "crm-ipc-detalle-title";
const OTRAS_ID = "crm-ipc-detalle-otras";
const ORIGEN_ID = "crm-ipc-detalle-origen";
const RESUMEN_ID = "crm-ipc-detalle-resumen";
// The block shows up to the 6 gestiones the server sends (04-diseno.md §5 "Detalle").
const OTRAS_MAX = 6;

const isBlank = (value) => value === null || value === undefined || String(value).trim() === "";
const textOr = (value) => (isBlank(value) ? <Muted /> : value);
const isAssigned = (gestion) => gestion?.responsableUsuarioId !== null && gestion?.responsableUsuarioId !== undefined;

function Item({ label, full = false, children }) {
  return (
    <div className={full ? "crm-rollo__sum-full" : undefined}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div className="crm-ipc__sum-group">
      <h4>{title}</h4>
      <dl className="crm-rollo__sum">{children}</dl>
    </div>
  );
}

const Deducido = () => (
  <>
    {" "}
    <Muted>(deducido)</Muted>
  </>
);

// Type of the summary: "—" for Proactiva, one muted "Tipo sin dato" for an IPC histórico without type (Prompt 6), and
// "(deducido)" when a histórico's type was deduced from its stored code.
function TipoValue({ gestion }) {
  if (isProactiva(gestion)) return <Muted />;
  const tipo = codeOf(gestion?.tipo);
  if (!tipo) return <Muted>Tipo sin dato</Muted>;
  return (
    <>
      {TIPO_LABELS[tipo] || gestion.tipo}
      {gestion.esHistorico ? <Deducido /> : null}
    </>
  );
}

// Motive: a histórico shows the deduced catalog motive, or its stored text when it could not be deduced.
function MotivoValue({ gestion }) {
  const code = codeOf(gestion?.motivoCodigo);
  if (gestion?.esHistorico && code) {
    return (
      <>
        {MOTIVO_LABELS[code] || gestion.motivoCodigo}
        <Deducido />
      </>
    );
  }
  const label = motivoLabel(gestion);
  return label === EMPTY ? <Muted /> : label;
}

function Duracion({ gestion, abierta }) {
  if (abierta) {
    const dias = Number(gestion?.diasAbierta) || 0;
    return (
      <>
        {dias === 0 ? "Hoy" : formatDuration(dias)} <Muted>(abierta)</Muted>
      </>
    );
  }
  return isBlank(gestion?.diasAbierta) ? <Muted /> : <>{formatDuration(gestion.diasAbierta)}</>;
}

// Read-only summary in groups (Prompt 5 "Resumen", plus the phone of the prototype): "—" for what is missing.
function Resumen({ gestion, timeline, abierta, currentUserId, today }) {
  const historico = Boolean(gestion?.esHistorico);
  const fecha = (value) => (isBlank(value) ? <Muted /> : historico ? formatDate(value) : dateTime(value));
  const origen = codeOf(gestion?.origen);
  return (
    <section aria-labelledby={RESUMEN_ID}>
      <h3 id={RESUMEN_ID} className="crm-rollo__sec-title">
        Resumen
      </h3>
      <Group title="Comercio">
        <Item label="Comercio">{textOr(gestion?.nombreComercio)}</Item>
        <Item label="CUIT">{isBlank(gestion?.cuit) ? <Muted /> : formatCuit(gestion.cuit)}</Item>
        <Item label="N° de comercio">{textOr(gestion?.numeroComercio)}</Item>
        <Item label="Terminal">{textOr(gestion?.numeroTerminal)}</Item>
        <Item label="Teléfono">{textOr(gestion?.telefono)}</Item>
      </Group>
      <Group title="Gestión">
        <Item label="Categoría">{isProactiva(gestion) ? "Proactiva" : "IPC"}</Item>
        <Item label="Tipo">
          <TipoValue gestion={gestion} />
        </Item>
        <Item label="Motivo">
          <MotivoValue gestion={gestion} />
        </Item>
        <Item label="Origen">{historico || !origen ? <Muted /> : labelFor(ORIGEN_LABELS, gestion.origen)}</Item>
        <Item label="Canal">{textOr(canalLabel(gestion?.canal))}</Item>
        {historico && gestion?.motivoCodigo && !isBlank(gestion?.motivoTexto) ? (
          <Item label="Texto guardado en el sistema anterior" full>
            {gestion.motivoTexto}
          </Item>
        ) : null}
        {historico ? (
          <Item label="Observación" full>
            {textOr(gestion?.observacion)}
          </Item>
        ) : null}
      </Group>
      <Group title="Estado">
        <Item label={historico ? "Estado original" : "Estado"}>
          <span className="crm-ipc__estado">
            <IpcStatusChip item={gestion} />
            {abierta ? <Actor estado={gestion?.estado} /> : null}
          </span>
        </Item>
        <Item label={abierta ? "Prioridad" : "Prioridad al cerrarse"}>
          <PrioridadCell item={gestion} />
        </Item>
        <Item label="Responsable">
          {isAssigned(gestion) ? (
            <>
              {gestion.responsableNombre || `Usuario ${gestion.responsableUsuarioId}`}
              {isMine(gestion, currentUserId) ? <span className="crm-rollo__muted crm-ipc__vos">(vos)</span> : null}
            </>
          ) : (
            <Muted>Sin asignar</Muted>
          )}
        </Item>
        {!abierta && !historico ? (
          <>
            <Item label="Motivo de cierre">{textOr(motivoCierreLabel(gestion))}</Item>
            <Item label="Observación de cierre" full>
              {textOr(closingObservacion(timeline))}
            </Item>
          </>
        ) : null}
      </Group>
      <Group title="Fechas">
        <Item label="Del contacto">{fecha(gestion?.fechaContacto)}</Item>
        <Item label="De registro">{fecha(gestion?.fechaAlta)}</Item>
        <Item label="Próximo contacto">
          {abierta && gestion?.fechaProximoContacto ? <ProximoContactoCell item={gestion} today={today} /> : <Muted />}
        </Item>
        <Item label="De cierre">{isBlank(gestion?.fechaCierre) ? <Muted /> : formatDate(gestion.fechaCierre)}</Item>
        <Item label="Duración">
          <Duracion gestion={gestion} abierta={abierta} />
        </Item>
      </Group>
    </section>
  );
}

// Origin of a gestión created by a process (Zoco Check, Sin facturación; R9): the process that created it and, without
// a responsible, the hint to take it.
function OrigenProceso({ gestion, abierta }) {
  const origen = codeOf(gestion?.origen);
  if (!origen || origen === "ASESOR" || gestion?.esHistorico) return null;
  return (
    <section className="crm-ipc__origen" aria-labelledby={ORIGEN_ID}>
      <h3 id={ORIGEN_ID} className="crm-rollo__sec-title">
        {`Creada desde ${labelFor(ORIGEN_LABELS, gestion.origen)}`}
      </h3>
      <dl className="crm-rollo__sum">
        <Item label="Proceso de origen">{isBlank(gestion?.procesoOrigenId) ? <Muted /> : `N° ${gestion.procesoOrigenId}`}</Item>
      </dl>
      {abierta && !isAssigned(gestion) ? <p className="crm-rollo__help">Llegó sin responsable. Tomala para empezar a gestionarla.</p> : null}
    </section>
  );
}

// "Otras gestiones de este comercio" (Prompt 5): open first, then the previous ones, históricos included; a table on
// desktop and a list on mobile. "Ver todas" filters the queue by the comercio's CUIT; "Ver" switches the Detalle to
// that gestión (as in the prototype).
function OtrasGestiones({ items, total, isMobile, onVerTodas, onVer }) {
  const shown = (items || []).slice(0, OTRAS_MAX);
  const count = Math.max(Number(total) || 0, shown.length);
  const estado = (item) => (
    <span className="crm-rollo__s-row crm-ipc__result">
      <IpcStatusChip item={item} />
      {item.esHistorico ? <HistoricoChip /> : null}
    </span>
  );

  const verButton = (item) =>
    onVer ? (
      <button type="button" className="crm-rollo__link" aria-label={`Ver la gestión ${motivoLabel(item)}`} onClick={() => onVer(item)}>
        Ver
      </button>
    ) : null;

  let body;
  if (!shown.length) {
    body = <p className="crm-rollo__sec-empty">Es la única gestión registrada para este comercio.</p>;
  } else if (isMobile) {
    body = (
      <ul className="crm-ipc__otras">
        {shown.map((item) => (
          <li key={`${item.esHistorico ? "h" : "g"}-${item.id}`}>
            <span className="crm-ipc__tipo-motivo">
              <TipoChip item={item} />
              <span>{motivoLabel(item)}</span>
            </span>
            {estado(item)}
            <span className="crm-rollo__s">{otraFecha(item)}</span>
            {verButton(item)}
          </li>
        ))}
      </ul>
    );
  } else {
    body = (
      <div className="crm-rollo__table-wrap">
        <table className="crm-rollo__table crm-rollo__table--sm">
          <thead>
            <tr>
              <th scope="col">Tipo</th>
              <th scope="col">Motivo</th>
              <th scope="col">Estado</th>
              <th scope="col">Fecha</th>
              {onVer ? (
                <th scope="col">
                  <span className="crm-rollo__sr-only">Acciones</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {shown.map((item) => (
              <tr key={`${item.esHistorico ? "h" : "g"}-${item.id}`}>
                <td>
                  <TipoChip item={item} />
                </td>
                <td>{motivoLabel(item)}</td>
                <td>{estado(item)}</td>
                <td className="crm-rollo__nowrap">{otraFecha(item)}</td>
                {onVer ? <td>{verButton(item)}</td> : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <section aria-labelledby={OTRAS_ID}>
      <div className="crm-rollo__sec-head">
        <h3 id={OTRAS_ID} className="crm-rollo__sec-title">
          Otras gestiones de este comercio
        </h3>
        {onVerTodas ? (
          <button type="button" className="crm-rollo__link" onClick={onVerTodas}>
            Ver todas
          </button>
        ) : null}
      </div>
      {body}
      {count > shown.length ? <div className="crm-rollo__help">{`Mostrando ${shown.length} de ${count}.`}</div> : null}
    </section>
  );
}

// A histórico has no detailed timeline: the old system only kept the registration and closing dates.
function HistoricoTimeline({ gestion }) {
  const cierre = gestion?.fechaCierre ? ` y la de cierre (${formatDate(gestion.fechaCierre)})` : "";
  return (
    <>
      <h3>Línea de tiempo</h3>
      <div className="crm-rollo__note crm-ipc__hist-note">
        <b>Sin historial detallado</b>
        <p>{`El sistema anterior solo guardaba la fecha de registro (${formatDate(gestion?.fechaAlta)})${cierre}.`}</p>
      </div>
    </>
  );
}

/**
 * "Detalle" of a gestión IPC or Proactiva (design Prompts 5, 6 and 7; T6): read-only. Loads GET gestiones/{id}, or for
 * a histórico (`row.esHistorico`, no detail endpoint, L18) builds it from the queue row and GET contexto of its alta.
 * Shows the stage, the header with the actions of an open gestión that can be managed (Abrir WhatsApp with the
 * comercio phone, Tomar / Liberar / Asignar… per D25, and Gestionar), the links band (Ver ficha and the motive buttons
 * of N8), the grouped summary, the origin process, "Otras gestiones de este comercio" and the timeline.
 *
 * onGestionar(gestion) and onVerTodas({ cuit, abiertas }) are expected to close this modal; onUpdated({ title, text })
 * runs after Tomar, Liberar or Asignar (the caller shows the toast and refreshes the queue), and the Detalle reloads.
 * A reload that fails (after a command or a version conflict) keeps the previous data and says so, with Reintentar.
 */
export default function DetalleIpcModal({
  gestionId,
  row = null,
  fechaCorteHistorico = null,
  responsables = [],
  currentUserId = null,
  darkMode = false,
  onClose,
  onGestionar,
  onVerTodas,
  onVerOtra,
  onUpdated,
  onNavigate,
}) {
  const isMobile = useIsMobile();
  const historico = Boolean(row?.esHistorico);
  const [detail, setDetail] = useState({ status: "loading", data: null, error: null });
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [freshIds, setFreshIds] = useState([]);
  // A failed reload keeps the previous data (and versionToken): "No pudimos recargar la gestión." until one succeeds.
  const [reloadFailed, setReloadFailed] = useState(false);
  const [reloading, setReloading] = useState(false);
  const requestRef = useRef(0);
  const mountedRef = useRef(true);
  const timelineRef = useRef([]);
  // The row and the cut-off date only seed a histórico; a new row object must not reload the Detalle.
  const seedRef = useRef({ row, fechaCorteHistorico });
  seedRef.current = { row, fechaCorteHistorico };

  useEffect(
    () => () => {
      mountedRef.current = false;
      requestRef.current += 1;
    },
    []
  );

  // Only the latest request may update the Detalle. A reload (after a command) keeps it and marks the new entries.
  const load = useCallback(
    async (reload = false) => {
      requestRef.current += 1;
      const requestId = requestRef.current;
      if (!reload) {
        setDetail({ status: "loading", data: null, error: null });
        setReloadFailed(false);
      }
      try {
        let response;
        if (historico) {
          const seed = seedRef.current;
          const contexto = seed.row?.clienteId ? await crmPostventaIpcApi.obtenerContexto(seed.row.clienteId) : null;
          response = historicoDetalle(seed.row, contexto, seed.fechaCorteHistorico);
        } else {
          response = (await crmPostventaIpcApi.obtenerGestion(gestionId)) || {};
        }
        if (!mountedRef.current || requestId !== requestRef.current) return;
        setFreshIds(reload ? freshEntryIds(timelineRef.current, response.timeline) : []);
        timelineRef.current = response.timeline || [];
        setDetail({ status: "done", data: response, error: null });
        setReloadFailed(false);
      } catch (loadError) {
        if (!mountedRef.current || requestId !== requestRef.current) return;
        if (reload) setReloadFailed(true);
        else setDetail({ status: "error", data: null, error: loadError });
      }
    },
    [gestionId, historico]
  );

  useEffect(() => {
    load();
  }, [load]);

  const loaded = detail.status === "done";
  const data = detail.data;
  const gestion = data?.gestion || row || null;
  const comercio = gestion?.nombreComercio || "";
  const versionToken = data?.versionToken || data?.gestion?.versionToken || null;
  const abierta = loaded && isAbierta(gestion);
  const canManage = abierta && data?.puedeGestionar !== false;
  const ownership = ownershipFor(data, gestion, canManage);
  const otras = data?.otrasGestiones || [];

  const owner = useOwnershipCommands({
    gestionId,
    versionToken,
    comercio,
    saving,
    setSaving,
    mountedRef,
    onStart: () => setConflict(null),
    onSuccess: async (title, text = comercio) => {
      setAssignOpen(false);
      onUpdated?.({ title, text });
      await load(true);
    },
    // Someone else changed the gestión: say so and show its current data.
    onConflict: (error) => {
      setAssignOpen(false);
      setConflict({ message: error?.message || "" });
      load(true);
    },
  });

  const retryReload = async () => {
    setReloading(true);
    await load(true);
    if (mountedRef.current) setReloading(false);
  };

  const whatsapp = canManage ? buildWhatsAppUrl(gestion?.telefono) : "";
  // A histórico of the list carries no comercio data of its own: it belongs to this comercio, so it takes it from here.
  const verOtra = onVerOtra
    ? (item) =>
        onVerOtra(
          item.esHistorico
            ? { ...item, clienteId: item.clienteId ?? gestion?.clienteId, cuit: item.cuit ?? gestion?.cuit, nombreComercio: item.nombreComercio ?? gestion?.nombreComercio }
            : item
        )
    : null;
  const verTodas = gestion?.cuit ? () => onVerTodas?.({ cuit: gestion.cuit, abiertas: comercioTieneAbiertas(gestion, otras) }) : null;

  const renderActions = () => {
    if (!canManage) return null;
    const button = (label, onClick, tone = "secondary") => (
      <button type="button" className={`crm-rollo__btn crm-rollo__btn--${tone} crm-rollo__btn--sm`} disabled={saving} onClick={onClick}>
        {label}
      </button>
    );
    return (
      <div className="crm-ipc__detalle-actions" role="group" aria-label="Acciones de la gestión">
        {whatsapp ? (
          <a className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm" href={whatsapp} target="_blank" rel="noopener noreferrer" title={gestion.telefono}>
            <FontAwesomeIcon icon={faWhatsapp} aria-hidden="true" />
            Abrir WhatsApp
          </a>
        ) : null}
        {ownership.tomar ? button("Tomar", owner.tomar) : null}
        {ownership.liberar ? button("Liberar", owner.liberar, "ghost") : null}
        {ownership.asignar ? button("Asignar…", () => setAssignOpen(true), "ghost") : null}
        {button("Gestionar", () => onGestionar?.(gestion), "primary")}
      </div>
    );
  };

  const headExtra =
    loaded && gestion ? (
      <>
        <span className="crm-rollo__s">{`CUIT ${formatCuit(gestion.cuit)}${gestion.numeroComercio ? ` · N° ${gestion.numeroComercio}` : ""}`}</span>
        <div className="crm-rollo__chips-row crm-ipc__detalle-chips">
          <TipoChip item={gestion} />
          <span>{motivoLabel(gestion)}</span>
          <IpcStatusChip item={gestion} />
          {abierta ? <Actor estado={gestion.estado} /> : null}
          {abierta ? <PrioridadCell item={gestion} /> : null}
          {gestion.esHistorico ? <HistoricoChip /> : null}
        </div>
        {renderActions()}
        <LinksBand gestion={gestion} editable={canManage} onNavigate={onNavigate} />
      </>
    ) : null;

  const renderAlerts = () => {
    const corte = data?.fechaCorteHistorico;
    const vencido = abierta && gestion?.contactoVencido && gestion?.fechaProximoContacto;
    return (
      <>
        {gestion?.esHistorico ? (
          <Alert tone="info" role="status" title="Registrada en el sistema anterior. Solo lectura.">
            {corte ? <span className="crm-ipc__alert-line">{`Registradas en el sistema anterior hasta el ${formatDate(corte)}.`}</span> : null}
            <span className="crm-ipc__alert-line">
              {gestion.motivoCodigo
                ? "El tipo y el motivo se dedujeron del texto guardado."
                : "El tipo y el motivo no se pudieron deducir: se muestra el texto guardado."}
            </span>
          </Alert>
        ) : null}
        {vencido ? (
          <Alert
            tone="info"
            role="status"
            title={`El próximo contacto venció ${formatRelativeDays(daysBetween(toIsoDate(gestion.fechaProximoContacto), todayIso()))} (${dateTime(gestion.fechaProximoContacto)}).`}
          >
            {gestion.prioridadManual ? "La prioridad está fijada a mano, así que no cambió." : "Por eso la prioridad pasó a Crítica."}
          </Alert>
        ) : null}
        {abierta && !canManage ? <Alert tone="info" role="status" title="No tenés permiso para gestionar esta gestión." /> : null}
        {conflict ? (
          <Alert tone="warn" role="alert" title={CONFLICT_TITLE}>
            {reloadFailed ? "Recargá para ver los cambios." : "Ya ves sus datos actuales."}
          </Alert>
        ) : null}
        {reloadFailed ? (
          <div className="crm-rollo__conflict">
            <Alert tone="error" role="alert" title="No pudimos recargar la gestión.">
              Probá de nuevo en unos segundos.
            </Alert>
            <div>
              <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm" disabled={reloading || saving} onClick={retryReload}>
                <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
                Reintentar
              </button>
            </div>
          </div>
        ) : null}
        {owner.error ? (
          <div className="crm-rollo__conflict">
            <Alert tone="error" role="alert" title={owner.error.title}>
              {owner.error.text}
            </Alert>
            {owner.error.retry && canManage ? (
              <div>
                <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm" disabled={saving} onClick={owner.retry}>
                  <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
                  Reintentar
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </>
    );
  };

  const renderMain = () => {
    if (detail.status === "loading") {
      return (
        <div className="crm-rollo__context-skel" aria-busy="true" aria-label="Cargando la gestión">
          {["60%", "85%", "45%", "70%"].map((width) => (
            <span key={width} className="crm-rollo__skel crm-rollo__skel--tall" style={{ width }} aria-hidden="true" />
          ))}
        </div>
      );
    }

    if (detail.status === "error") {
      return (
        <div className="crm-rollo__note crm-rollo__note--error" role="alert">
          <span className="crm-rollo__note-ico" aria-hidden="true">
            <FontAwesomeIcon icon={faTriangleExclamation} />
          </span>
          <b>No pudimos cargar la gestión</b>
          <p>Hubo un problema de conexión con el servidor. Probá de nuevo en unos segundos.</p>
          <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm" onClick={() => load()}>
            <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
            Reintentar
          </button>
        </div>
      );
    }

    return (
      <>
        {renderAlerts()}
        <Resumen gestion={gestion} timeline={data?.timeline} abierta={abierta} currentUserId={currentUserId} today={todayIso()} />
        <OrigenProceso gestion={gestion} abierta={abierta} />
        <OtrasGestiones items={otras} total={data?.totalOtrasGestiones} isMobile={isMobile} onVerTodas={verTodas} onVer={verOtra} />
      </>
    );
  };

  let footNote = "";
  if (loaded) footNote = canManage ? "Vista de solo lectura. Para avanzarla, usá Gestionar." : "Solo lectura";

  return (
    <>
      <Modal
        titleId={TITLE_ID}
        title={comercio || "Gestión"}
        stepLabel={loaded ? `Detalle de gestión · ${detalleStage(gestion)}` : "Detalle de gestión"}
        size="lg"
        className={`crm-ipc crm-ipc-detalle${isMobile ? " crm-ipc-detalle--mobile" : ""}`}
        darkMode={darkMode}
        closeDisabled={saving || assignOpen}
        onClose={onClose}
        headExtra={headExtra}
        bodyClassName="crm-rollo__manage"
        footer={
          <>
            <span className="crm-rollo__grow crm-rollo__help">{footNote}</span>
            <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary" disabled={saving} onClick={onClose}>
              Cerrar
            </button>
          </>
        }
      >
        <div className="crm-rollo__manage-main">{renderMain()}</div>
        <aside className="crm-rollo__manage-side" aria-label="Línea de tiempo">
          {loaded && gestion?.esHistorico ? <HistoricoTimeline gestion={gestion} /> : null}
          {loaded && !gestion?.esHistorico ? <Timeline entries={data?.timeline} gestion={gestion} freshIds={freshIds} /> : null}
        </aside>
      </Modal>

      {assignOpen && gestion ? (
        <AsignarGestionDialog
          gestion={gestion}
          responsables={responsables}
          currentUserId={currentUserId}
          darkMode={darkMode}
          onClose={() => setAssignOpen(false)}
          onSubmit={owner.asignar}
        />
      ) : null}
    </>
  );
}
